import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Eye, EyeOff, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSelector } from '../components/LanguageSelector';
import { safeStorage, userTimerSlice } from '../lib/safeStorage';
import {
  fetchHasPasskey,
  isWebAuthnSupported,
  useWebAuthn,
  PASSKEY_PROMPT_DISMISSED_KEY,
} from '../lib/webauthn';
import PasskeyEnrolmentDialog from '../components/PasskeyEnrolmentDialog';
import useTheme4A from '../components/useTheme4A';
import { WaveTrail } from '../components/Theme4A';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function LoginPage() {
  // fixedDecor off: LoginPage has no fixed top header and no bottom menu
  // bar. The static wave strip at the end is kept.
  useTheme4A({ fixedDecor: false });
  const { user, login } = useAuth();
  const navigate = useNavigate();
  // Show the medical disclaimer only the first time on this device.  Persisting
  // the dismissal removes a major source of beta-tester friction (the modal
  // used to re-appear on every visit to /login, which felt like a paywall).
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    try { return safeStorage.getItem('medical_disclaimer_ack') !== 'true'; }
    catch (_) { return true; }
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginPin, setLoginPin] = useState('');
  const [usePin, setUsePin] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Register state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regSobrietyDate, setRegSobrietyDate] = useState('');
  const [regSobrietyTime, setRegSobrietyTime] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  
  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  
  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  // Passkey state
  const [emailHasPasskey, setEmailHasPasskey] = useState(false);
  const [showPasskeyEnrolment, setShowPasskeyEnrolment] = useState(false);
  const passkey = useWebAuthn();
  const webAuthnSupported = isWebAuthnSupported();

  // Lookup whether the entered email has a passkey (debounced lightly via blur).
  const refreshHasPasskey = React.useCallback(async (email) => {
    if (!webAuthnSupported || !email) {
      setEmailHasPasskey(false);
      return;
    }
    const has = await fetchHasPasskey(email);
    setEmailHasPasskey(has);
  }, [webAuthnSupported]);

  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handlePasskeyLogin = async () => {
    if (!loginEmail) {
      toast.error('Email required');
      return;
    }
    try {
      const data = await passkey.signInWithPasskey(loginEmail);
      login(data.user);
      toast.success('Welcome back');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.message || 'Passkey sign-in failed');
    }
  };

  const handleLogin = async () => {
    if (!loginEmail) {
      toast.error('Email required');
      return;
    }
    
    if (!usePin && !loginPassword) {
      toast.error('Password required');
      return;
    }
    
    if (usePin && !loginPin) {
      toast.error('PIN required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: loginEmail,
          password: usePin ? null : loginPassword,
          pin: usePin ? loginPin : null,
          totp_code: totpCode || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Check if 2FA is required
        if (data.requires_2fa) {
          setNeeds2FA(true);
          toast.info('Enter your Google Authenticator code');
          return;
        }
        safeStorage.setItem('token', data.token);
        safeStorage.setItem('user', JSON.stringify(userTimerSlice(data.user)));
        safeStorage.setItem('invite_verified', 'true');
        login(data.user);
        toast.success('Welcome back');

        // Offer passkey enrolment after a successful PASSWORD login when:
        //  - the browser supports WebAuthn
        //  - the user hasn't already enrolled a passkey
        //  - they haven't previously dismissed/skipped the prompt
        const dismissed = safeStorage.getItem(PASSKEY_PROMPT_DISMISSED_KEY);
        if (
          webAuthnSupported &&
          !emailHasPasskey &&
          !dismissed &&
          !data.user?.totp_enabled // already 2FA: don't pile on
        ) {
          setShowPasskeyEnrolment(true);
          // Don't navigate yet — dialog onClose will navigate.
          return;
        }

        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An error occurred');
    }
  };

  const handleRegister = async () => {
    if (!regEmail || !regPassword || !regName) {
      toast.error('All fields required');
      return;
    }

    if (regPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: regEmail,
          password: regPassword,
          name: regName,
          sobriety_date: regSobrietyDate || null,
          sobriety_time: regSobrietyTime || null,
          invite_code: safeStorage.getItem('beta_invite_code') || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          safeStorage.setItem('token', data.token);
          safeStorage.setItem('user', JSON.stringify(userTimerSlice(data.user)));
          safeStorage.setItem('invite_verified', 'true');
        }
        login(data.user);
        toast.success('Account created');
        navigate('/set-pin');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error('An error occurred');
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Email required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      if (response.ok) {
        setResetSent(true);
        toast.success('Password reset email sent');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('An error occurred');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Medical Disclaimer Modal */}
      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent className="max-w-md" data-testid="medical-disclaimer-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-zinc-950 uppercase tracking-tight">Medical Disclaimer</DialogTitle>
            <DialogDescription className="text-sm text-zinc-700 leading-relaxed mt-4">
              This tool is an aid for Recovery and does not replace professional medical advice or supervised detoxification.
              <br /><br />
              If you are experiencing a medical emergency or acute withdrawal symptoms, please contact emergency services immediately.
              <br /><br />
              <strong>Your data is sent over encrypted connections. The Privacy Policy explains what we keep and why.</strong>
            </DialogDescription>
          </DialogHeader>
          <Button
            onClick={() => {
              try { safeStorage.setItem('medical_disclaimer_ack', 'true'); } catch (_) { /* memory fallback ok */ }
              setShowDisclaimer(false);
            }}
            className="rounded-md h-12 px-6 font-bold text-base border-2 border-black text-black hover:brightness-95 mt-4" style={{ backgroundColor: '#ffcd04' }}
            data-testid="acknowledge-disclaimer-btn"
            data-arp="btn-primary"
          >
            I Acknowledge
          </Button>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-md" data-testid="forgot-password-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-zinc-950 uppercase tracking-tight">Reset Password</DialogTitle>
            <DialogDescription className="text-sm text-zinc-700 leading-relaxed mt-4">
              {resetSent ? (
                <>
                  Password reset instructions have been sent to your verified email address.
                  <br /><br />
                  Please check your inbox and follow the link to reset your password.
                </>
              ) : (
                'Enter your verified email address and we\'ll send you instructions to reset your password.'
              )}
            </DialogDescription>
          </DialogHeader>
          {!resetSent && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Email Address</Label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                onClick={handleForgotPassword}
                className="w-full rounded-md h-12 font-bold text-base border-2 border-black text-black hover:brightness-95" style={{ backgroundColor: '#ffcd04' }}
                data-testid="send-reset-btn"
                data-arp="btn-primary"
              >
                Send Reset Link
              </Button>
            </div>
          )}
          {resetSent && (
            <Button
              onClick={() => {
                setShowForgotPassword(false);
                setResetSent(false);
                setForgotEmail('');
              }}
              className="w-full rounded-md h-12 font-bold text-base border-2 border-black text-black hover:brightness-95" style={{ backgroundColor: '#ffcd04' }}
              data-arp="btn-primary"
            >
              Close
            </Button>
          )}
        </DialogContent>
      </Dialog>

      {/* Language Selector */}
      <div className="fixed top-4 right-4 z-40">
        <LanguageSelector />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 text-center">
            <img src={AXIOM_LOGO} alt="Axiom" className="h-20 mx-auto" data-testid="axiom-logo" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold tracking-tight text-black text-center mb-2">
            Recovery Programme
          </h1>
          <p className="text-base font-normal text-black leading-relaxed text-center mb-8">
            30 week programme with 24/7 digital sponsor bot support<br />
            Full accountability for criminal justice system reports
          </p>

          {/* Auth Tabs */}
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <div className="bg-white rounded-md p-6 space-y-4">
                <div>
                  <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Email</Label>
                  <Input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    onBlur={(e) => refreshHasPasskey(e.target.value)}
                    autoComplete="username webauthn"
                    className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12"
                    data-testid="login-email-input"
                  />
                </div>

                {!usePin ? (
                  <div>
                    <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Password</Label>
                    <div className="relative">
                      <Input
                        type={showLoginPassword ? 'text' : 'password'}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12 pr-10"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
                        data-testid="toggle-login-password-visibility"
                      >
                        {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">4-Digit PIN</Label>
                    <Input
                      type="password"
                      maxLength={4}
                      value={loginPin}
                      onChange={(e) => setLoginPin(e.target.value.replace(/\D/g, ''))}
                      className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12 text-center text-2xl tracking-widest"
                      data-testid="login-pin-input"
                    />
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <button
                    onClick={() => setUsePin(!usePin)}
                    className="text-sm text-blue-600 hover:underline"
                    data-testid="toggle-pin-btn"
                  >
                    {usePin ? 'Use password instead' : 'Use PIN instead'}
                  </button>
                  <button
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-zinc-600 hover:underline"
                    data-testid="forgot-password-btn"
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Public "Get help now": no auth required (Sep 2026 spec). */}
                <div className="text-center pt-2">
                  <a
                    href="/get-help"
                    className="text-sm text-rose-700 hover:underline font-semibold"
                    data-testid="login-get-help-now-link"
                  >
                    Get help now
                  </a>
                </div>

                {/* 2FA Code Input - appears when required */}
                {needs2FA && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4" data-testid="2fa-login-section">
                    <Label className="text-xs font-bold text-blue-900 uppercase mb-2 block">Google Authenticator Code</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="bg-white border border-blue-300 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-md h-12 text-center text-2xl tracking-[0.5em] font-mono"
                      data-testid="login-totp-input"
                      autoFocus
                    />
                    <p className="text-xs text-blue-700 mt-2">Enter the 6-digit code from your authenticator app</p>
                  </div>
                )}

                <Button
                  onClick={handleLogin}
                  className="w-full rounded-md h-12 font-bold text-base border-2 border-black text-black hover:brightness-95" style={{ backgroundColor: '#ffcd04' }}
                  data-testid="login-submit-btn"
                  data-arp="btn-primary"
                >
                  {needs2FA ? 'Verify & Sign In' : 'Sign In'}
                </Button>

                {/* Passkey sign-in: only shown when the entered email has a passkey enrolled. */}
                {webAuthnSupported && emailHasPasskey && !needs2FA && (
                  <>
                    <div className="flex items-center gap-2 my-1" aria-hidden="true">
                      <div className="flex-1 h-px bg-zinc-200" />
                      <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">or</span>
                      <div className="flex-1 h-px bg-zinc-200" />
                    </div>
                    <Button
                      type="button"
                      onClick={handlePasskeyLogin}
                      disabled={passkey.busy}
                      variant="outline"
                      className="w-full h-12 border-2 border-zinc-950 text-zinc-950 hover:bg-zinc-50 rounded-md font-medium text-sm tracking-wide"
                      data-testid="login-passkey-btn"
                    >
                      <Fingerprint className="w-4 h-4 mr-2" />
                      {passkey.busy ? 'Verifying…' : 'Sign in with Face ID / Fingerprint'}
                    </Button>
                  </>
                )}
              </div>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <div className="bg-white rounded-md p-6 space-y-4">
                <div>
                  <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Full Name</Label>
                  <Input
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12"
                    data-testid="register-name-input"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Email</Label>
                  <Input
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12"
                    data-testid="register-email-input"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Password (min 8 characters)</Label>
                  <div className="relative">
                    <Input
                      type={showRegPassword ? 'text' : 'password'}
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12 pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegPassword(!showRegPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
                      data-testid="toggle-register-password-visibility"
                    >
                      {showRegPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Sobriety Date (Optional)</Label>
                  <Input
                    type="date"
                    value={regSobrietyDate}
                    onChange={(e) => setRegSobrietyDate(e.target.value)}
                    className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12"
                    data-testid="register-sobriety-date-input"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Sobriety Time (Optional)</Label>
                  <Input
                    type="time"
                    value={regSobrietyTime}
                    onChange={(e) => setRegSobrietyTime(e.target.value)}
                    className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12"
                    data-testid="register-sobriety-time-input"
                  />
                </div>

                <Button
                  onClick={handleRegister}
                  className="w-full rounded-md h-12 font-bold text-base border-2 border-black text-black hover:brightness-95" style={{ backgroundColor: '#ffcd04' }}
                  data-testid="register-submit-btn"
                  data-arp="btn-primary"
                >
                  Create Account
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {/* Footer Note */}
          <p className="text-base font-medium text-black text-center mt-8">
            Clinical. Direct. Accountable.
          </p>
          <p className="text-base text-black text-center mt-2">
            AUD 24.70 a week or AUD 690 upfront. Lifetime access comes with the upfront payment or after 28 weekly payments.
          </p>
        </div>
      </div>

      {/* Post-login passkey enrolment prompt (one-time) */}
      <PasskeyEnrolmentDialog
        open={showPasskeyEnrolment}
        onClose={() => {
          setShowPasskeyEnrolment(false);
          navigate('/dashboard');
        }}
      />
      {/* 4a: static wave strip at the bottom of this bar-less page. */}
      <WaveTrail variant="static" />
    </div>
  );
}

export default LoginPage;